import React, { useState } from 'react';
import { notification, Space, Tooltip, Typography } from 'antd';
import type { TablePaginationConfig, TableProps } from 'antd';
import {
  IShippingAddress,
  ISearchShippingAddressReq,
  ISearchShippingAddressRes,
} from '@/interfaces/shipping-address';
import { EditFilled, DeleteFilled } from '@ant-design/icons';
import { deleteShippingAddress } from '@/adapters/shipping-address';
import { IDataModal } from '@/interfaces/dataModal';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import TableComponent from '@/components/table';

interface IProps {
  loading: boolean;
  criteria: ISearchShippingAddressReq;
  ShippingAddress: ISearchShippingAddressRes;
  onDelete: (id: number) => void;
  onEditData: (item: IShippingAddress) => void;
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IShippingAddress> | SorterResult<IShippingAddress>[],
  ) => void;
}

const _ShippingAddressModal: IDataModal<IShippingAddress> = {
  open: false,
};

const ShippingAddressTable = ({
  loading,
  criteria,
  ShippingAddress,
  onDelete,
  onEditData,
  onTableChange,
}: IProps) => {
  const [deleteModal, setDeleteModal] = useState<IDataModal<IShippingAddress>>(
    _ShippingAddressModal,
  );

  const onDeleteShippingAddress = async () => {
    try {
      await deleteShippingAddress(Number(deleteModal?.id));
      notification.success({
        message: 'ลบที่อยู่สำเร็จ',
      });
      onDelete(Number(deleteModal?.id || 0));
      setDeleteModal({ ..._ShippingAddressModal });
    } catch (error) {
      console.log(error);
    }
  };

  const columns: TableProps<IShippingAddress>['columns'] = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 40,
      align: 'center',
      className: 'text-center',
      render: (_, rec, i) => <Space>{i + 1}</Space>,
    },
    {
      title: 'ชื่อย่อ',
      dataIndex: 'shortName',
      key: 'shortName',
      fixed: 'left',
      className: 'font-bold',
    },
    {
      title: 'สถานที่',
      dataIndex: 'name',
      key: 'name',
      className: 'font-bold',
    },
    {
      title: 'ที่อยู่',
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: 'ถนน',
      dataIndex: 'road',
      key: 'road',
    },
    {
      title: 'ตำบล',
      dataIndex: 'subdistrictNameTh',
      key: 'subdistrictNameTh',
      width: 200,
      // render: (_, rec) => (
      //   <div className="flex flex-col items-start">
      //     <div>{_}</div>
      //     <div className="text-gray-400 text-xs">{rec.subdistrictNameEn}</div>
      //   </div>
      // ),
    },
    {
      title: 'อำเภอ',
      dataIndex: 'districtNameTh',
      key: 'districtNameTh',
      width: 180,
      // render: (_, rec) => (
      //   <div className="flex flex-col items-start">
      //     <div>{_}</div>
      //     <div className="text-gray-400 text-xs">{rec.districtNameEn}</div>
      //   </div>
      // ),
    },
    {
      title: 'จังหวัด',
      dataIndex: 'provinceNameTh',
      key: 'provinceNameTh',
      width: 130,
      // render: (_, rec) => (
      //   <div className="flex flex-col items-start">
      //     <div>{_}</div>
      //     <div className="text-gray-400 text-xs">{rec.provinceNameEn}</div>
      //   </div>
      // ),
    },
    {
      title: 'ผู้ติดต่อ',
      dataIndex: 'contactName',
      key: 'contactName',
      width: 140,
    },
    {
      title: 'เบอร์โทร',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: 140,
    },
    {
      title: 'Google map',
      dataIndex: 'maplink',
      key: 'maplink',
      width: 100,
      align: 'center',
      render: _ =>
        _ ? (
          <Typography.Link
            href={_}
            target="_blank"
            copyable={{ text: _ }}
            className="underline text-blue-700 cursor-pointer"
          >
            map link
          </Typography.Link>
        ) : (
          'ไม่พบ Map link'
        ),
    },
    {
      title: 'Remark',
      dataIndex: 'remark',
      key: 'remark',
      align: 'center',
      width: 100,
      render: _ => (
        <Tooltip placement="top" title={_}>
          <p className="truncate "> {_}</p>
        </Tooltip>
      ),
    },
    {
      title: 'ส่งภายใน',
      dataIndex: 'dueDateTime',
      key: 'dueDateTime',
      align: 'center',
      width: 100,
    },
    {
      dataIndex: 'action',
      key: 'action',
      width: 80,
      render: (_, rec) => (
        <div className="flex flex-row gap-4 items-center w-full justify-center">
          <EditFilled
            className="text-blue-500"
            onClick={() => onEditData(rec)}
          />
          <DeleteFilled
            className="text-red-700"
            onClick={() =>
              setDeleteModal({
                open: true,
                item: rec,
                id: rec.id,
              })
            }
          />
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<IShippingAddress>
        columns={columns}
        criteria={criteria}
        data={ShippingAddress}
        loading={loading}
        onTableChange={onTableChange}
        showPagination={false}
        scroll
        scrollLength={1800}
      />
      <DeleteConfirmModal
        content={`คุณต้องการลบลูกค้า '${deleteModal?.item?.name}' ใช่หรือไม่`}
        handleCancel={() => {
          setDeleteModal({ ..._ShippingAddressModal });
        }}
        handleOk={() => {
          onDeleteShippingAddress();
        }}
        open={deleteModal?.open}
        title={`ลูกค้า '${deleteModal?.item?.name}'`}
      />
    </div>
  );
};

export default ShippingAddressTable;
